/**
 * ConfirmStep.js
 *
 * Step 7: Final confirmation before import.
 */

import React from 'react';
import { CheckCircle, FileText, User, Clock, List, Shield, AlertTriangle } from 'lucide-react';
import { CHECKLIST_TYPE_CONFIG } from '../../../../../../config/operations/checklistImportConfig';

const ConfirmStep = ({
  checklistType,
  items = [],
  approvalData,
  extractionResult,
  uploadedFile
}) => {
  const typeConfig = CHECKLIST_TYPE_CONFIG[checklistType];

  // Count required vs optional items
  const requiredCount = items.filter(i => i.required).length;
  const optionalCount = items.length - requiredCount;

  // Check for low confidence items
  const lowConfidenceItems = items.filter(i => i.confidence && i.confidence < 0.7);

  return (
    <div className="p-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800">Ready to Import</h3>
        <p className="text-sm text-slate-500 mt-1">
          Review the summary below and confirm the import
        </p>
      </div>

      {/* Summary cards */}
      <div className="space-y-4">

        {/* Checklist type */}
        <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
          <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <List className="h-5 w-5 text-cyan-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-slate-500">Checklist Type</p>
            <p className="font-semibold text-slate-800">{typeConfig?.label || checklistType}</p>
            <p className="text-sm text-slate-500 mt-1">
              {extractionResult?.title || 'Imported Checklist'}
            </p>
          </div>
        </div>

        {/* Items count */}
        <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-slate-500">Items to Import</p>
            <p className="font-semibold text-slate-800">{items.length} items</p>
            <p className="text-sm text-slate-500 mt-1">
              {requiredCount} required • {optionalCount} optional
            </p>
          </div>
        </div>

        {/* Source document */}
        <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
          <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
            <FileText className="h-5 w-5 text-slate-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-slate-500">Source Document</p>
            <p className="font-semibold text-slate-800 truncate">{uploadedFile?.name || 'Unknown'}</p>
            <p className="text-sm text-slate-500 mt-1">
              {uploadedFile ? `${(uploadedFile.size / 1024 / 1024).toFixed(2)} MB` : ''}
              {approvalData?.documentRef && ` • Ref: ${approvalData.documentRef}`}
            </p>
          </div>
        </div>

        {/* Approved by */}
        <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
          <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <User className="h-5 w-5 text-cyan-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-slate-500">Approved By</p>
            <p className="font-semibold text-slate-800">{approvalData?.approverName || '---'}</p>
            <p className="text-sm text-slate-500 mt-1 capitalize">
              {approvalData?.approverRole?.replace('_', ' ') || '---'}
            </p>
          </div>
          {approvalData?.signature && (
            <img
              src={approvalData.signature}
              alt="Signature"
              className="h-12 border border-slate-200 rounded bg-white"
            />
          )}
        </div>

        {/* Timestamp */}
        <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
          <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
            <Clock className="h-5 w-5 text-slate-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-slate-500">Import Time</p>
            <p className="font-semibold text-slate-800">
              {new Date().toLocaleString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Low confidence warning */}
      {lowConfidenceItems.length > 0 && (
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                {lowConfidenceItems.length} item(s) have low extraction confidence
              </p>
              <p className="text-sm text-amber-700 mt-1">
                You may want to double-check these items after import.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Audit trail notice */}
      <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-green-800">
            <p className="font-medium">Audit Trail</p>
            <p className="mt-1">
              This import will be logged with full traceability including document hash,
              extraction metadata, and approval signature for regulatory compliance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmStep;
